import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, CircularProgress, TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useProjectConfig, useUpdateProjectConfig } from '../../../api/hooks/useProjectConfig';
import { useProjectInitialization } from '../../../api/hooks/useProjectInitialization';
import { useProject } from '../../../api/hooks/useProjects';
import { useProjectTeam } from '../../../api/hooks/useProjectTeam';
import { useUnsicherheiten } from '../../../api/hooks/useUnsicherheiten';
import { useUpsertReifegrad, computeMaturityFromForm } from '../../../api/hooks/useReifegrad';
import { useAutosave } from '../../../hooks/useAutosave';
import { PmCard } from '../../../components/pm';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { useNotifications } from '../../../context/NotificationContext';
import { buildProjektauftragReport } from '../reports/projektauftragReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';
import { ZieleTab } from './ZieleTab';
import { TeamTab } from './TeamTab';
import {
  ALL_AXES, MAX_FIXED, cellOf, centerOf, staceyPointFromConfig, staceyToConfig,
  MagicTriangleCard, StaceyCard, VertragCard,
  type Axis, type StaceyPoint,
} from './ProjektCharakter';
import { ZeitenTab } from './ZeitenTab';

interface ConfigData {
  magic_triangle_fixed: Axis[];
  magic_triangle_flex: Axis[];
  stacey_x: number;
  stacey_y: number;
}

interface PaForm {
  auftraggeber: string;
  sponsor: string;
  vertrag: string;
  rahmenbedingungen: string;
}

/** Kombinierter Autosave-Wert: Organisation + Dreieck + Stacey. */
interface PaValues {
  paForm: PaForm;
  fixed: Axis[];
  point: StaceyPoint;
}

export function ProjektauftragTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { notify } = useNotifications();

  const { data, isLoading } = useProjectConfig(projectId);
  const updateMut = useUpdateProjectConfig(projectId);
  const config = ((data as unknown as { data?: ConfigData } | undefined)?.data ?? null);

  // Auftraggeber / Sponsor / Vertrag / Rahmenbedingungen leben in project_initialization
  const init = useProjectInitialization(projectId);
  const upsertReifegrad = useUpsertReifegrad(projectId);

  // Zusatzquellen nur für den PDF-Report (Name, Team, Risiken-Feld der Ziele).
  const { data: projectResp } = useProject(projectId);
  const { data: teamResp } = useProjectTeam(projectId);
  const unc = useUnsicherheiten(projectId);
  const projectName =
    (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';
  const teamItems =
    (teamResp as unknown as { data?: { name: string; rolle: string | null; funktion: string | null; email: string | null }[] } | undefined)?.data ?? [];

  const [fixed, setFixed] = useState<Axis[]>([]);
  // Freier Stacey-Punkt (PHQ-77); die Zelle (staceyX/Y) ergibt sich aus der Lage.
  const [staceyPt, setStaceyPt] = useState<StaceyPoint>(centerOf(0, 3));
  const { x: staceyX, y: staceyY } = cellOf(staceyPt);
  const [paForm, setPaForm] = useState<PaForm>({
    auftraggeber: '',
    sponsor: '',
    vertrag: '',
    rahmenbedingungen: '',
  });

  const autosave = useAutosave<PaValues>({
    onSave: async (v) => {
      const flexAxes = ALL_AXES.filter((a) => !v.fixed.includes(a));
      await updateMut.mutateAsync({
        magic_triangle_fixed: v.fixed,
        magic_triangle_flex: flexAxes,
        ...staceyToConfig(v.point),
      });
      await init.save({
        projektauftrag_auftraggeber: v.paForm.auftraggeber.trim(),
        projektauftrag_sponsor: v.paForm.sponsor.trim(),
        projektauftrag_vertrag: v.paForm.vertrag.trim(),
        projektauftrag_rahmenbedingungen: v.paForm.rahmenbedingungen.trim(),
      });
      const filled = [
        v.paForm.auftraggeber.trim().length > 0,
        v.paForm.sponsor.trim().length > 0,
        v.paForm.vertrag.trim().length > 0,
        v.paForm.rahmenbedingungen.trim().length > 0,
        v.fixed.length === 2,
      ];
      const level = computeMaturityFromForm(filled, [true, true, false, false, true]);
      try { await upsertReifegrad.mutateAsync({ schritt: 3, data: { reifegrad: level } }); } catch { /* best-effort */ }
    },
  });

  const fixedKey = config?.magic_triangle_fixed?.join(',') ?? '';
  const cfgStaceyX = config?.stacey_x ?? null;
  const cfgStaceyY = config?.stacey_y ?? null;
  useEffect(() => {
    if (autosave.isDirty) return; // nicht waehrend des Editierens ueberschreiben
    if (!config) return;
    const f = (config.magic_triangle_fixed ?? []).filter((a): a is Axis => ALL_AXES.includes(a)).slice(0, MAX_FIXED);
    setFixed(f);
    setStaceyPt(staceyPointFromConfig(config.stacey_x, config.stacey_y));
  }, [autosave.isDirty, fixedKey, cfgStaceyX, cfgStaceyY]);

  useEffect(() => {
    if (autosave.isDirty) return;
    setPaForm({
      auftraggeber: init.data.projektauftrag_auftraggeber,
      sponsor: init.data.projektauftrag_sponsor,
      vertrag: init.data.projektauftrag_vertrag,
      rahmenbedingungen: init.data.projektauftrag_rahmenbedingungen,
    });
  }, [
    autosave.isDirty,
    init.data.projektauftrag_auftraggeber,
    init.data.projektauftrag_sponsor,
    init.data.projektauftrag_vertrag,
    init.data.projektauftrag_rahmenbedingungen,
  ]);

  const toggleAxis = (axis: Axis) => {
    let nextFixed: Axis[];
    if (fixed.includes(axis)) {
      nextFixed = fixed.filter((a) => a !== axis);
    } else {
      if (fixed.length >= MAX_FIXED) {
        notify(t('projektauftrag.errorMaxFixed', 'Maximal 2 Achsen können gleichzeitig fix sein.'), 'warning');
        return;
      }
      nextFixed = [...fixed, axis];
    }
    setFixed(nextFixed);
    autosave.schedule({ paForm, fixed: nextFixed, point: staceyPt });
  };

  const setStacey = (p: StaceyPoint) => {
    setStaceyPt(p);
    autosave.schedule({ paForm, fixed, point: p });
  };

  const updatePa = (patch: Partial<PaForm>) => {
    const nextPa = { ...paForm, ...patch };
    setPaForm(nextPa);
    autosave.schedule({ paForm: nextPa, fixed, point: staceyPt });
  };

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('projektauftrag.title', 'Projektauftrag') };
  const buildReport = () =>
    buildProjektauftragReport({
      auftraggeber: paForm.auftraggeber,
      sponsor: paForm.sponsor,
      vertrag: paForm.vertrag,
      rahmenbedingungen: paForm.rahmenbedingungen,
      zieleHaupt: init.data.ziele_haupt,
      zieleTeil: init.data.ziele_teil,
      zieleNicht: init.data.ziele_nicht,
      zieleAkzeptanz: init.data.ziele_akzeptanz,
      bekannteUnbekannte: unc.data.bekannte_unbekannte,
      team: teamItems,
      zeitenStart: init.data.zeiten_start_date,
      zeitenEnde: init.data.zeiten_end_date,
      zeitenPflichttermine: init.data.zeiten_pflichttermine,
      zeitenBeschraenkungen: init.data.zeiten_beschraenkungen,
      fixed,
      staceyX,
      staceyY,
    });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h6">{t('projektauftrag.title', 'Projektauftrag')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('projektauftrag.longHint', 'Lange Variante — enthält die Inhalte von Ziele, Team und Zeiten, synchron mit den Einzelseiten.')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PdfExportButton meta={pdfMeta} build={buildReport} />
          <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} />
        </Stack>
      </Stack>

      {/* Auftrag & Organisation */}
      <PmCard>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('projektauftrag.organisation', 'Auftrag & Organisation')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('projektauftrag.organisationHint', 'Auftraggeber / Sponsor, Kunde und vertragliche Rahmenbedingungen des Projekts.')}
        </Typography>
        <Stack spacing={2.5}>
          <TextField
            label={t('projektauftrag.auftraggeber', 'Auftraggeber / Sponsor')}
            placeholder="z.B. Ritter Technologie GmbH – Geschäftsführung"
            slotProps={{ inputLabel: { shrink: true } }}
            value={paForm.auftraggeber}
            onChange={(e) => updatePa({ auftraggeber: e.target.value })}
            fullWidth
          />
          <TextField
            label={t('projektauftrag.sponsor', 'Kunde')}
            placeholder="z.B. Kunde GmbH"
            slotProps={{ inputLabel: { shrink: true } }}
            value={paForm.sponsor}
            onChange={(e) => updatePa({ sponsor: e.target.value })}
            fullWidth
          />
          <TextField
            label={t('projektauftrag.rahmenbedingungen', 'Sonstige Rahmenbedingungen')}
            placeholder="z.B. Hybrid-Methodik vereinbart, 18-Schritte-Framework als Leitfaden"
            slotProps={{ inputLabel: { shrink: true } }}
            value={paForm.rahmenbedingungen}
            onChange={(e) => updatePa({ rahmenbedingungen: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
        </Stack>
      </PmCard>

      {/* Eingebettete Sektionen — gleiche Komponenten/Hooks wie die Einzelseiten,
          daher automatisch synchron (geteilter React-Query-Cache). */}
      <ZieleTab projectId={projectId} embedded />

      {/* Vertragsbedingungen / Auftragstyp — bewusst ans Ende der Ziele verschoben. */}
      <VertragCard value={paForm.vertrag} onChange={(v) => updatePa({ vertrag: v })} />

      {/* Magisches Dreieck + Stacey-Modell — oberhalb des Teams (Projekt-Charakterisierung). */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>
        <MagicTriangleCard fixed={fixed} onToggle={toggleAxis} />
        <StaceyCard point={staceyPt} onChange={setStacey} />
      </Box>

      <TeamTab projectId={projectId} embedded />
      <ZeitenTab projectId={projectId} embedded />
    </Stack>
  );
}
